import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Button, Modal, Input, message, Space, Statistic, Row, Col, Avatar, Form, Divider } from 'antd';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserIdAsNumber, getUserRole } from '../utils/cognitoUtils';
import './MainPage.css';
import { 
  PlusCircleOutlined, 
  FileTextOutlined, 
  CalendarOutlined, 
  TeamOutlined, 
  EditOutlined, 
  CrownOutlined,
  FireOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  CheckSquareOutlined,
  CustomerServiceOutlined,
  SearchOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import apiService, { 
  Lead, 
  getMainPageContent, 
  updateMainPageContent,
  getNews,
  createNews,
  updateNews,
  deleteNews,
  getCategories,
  createCategory,
  deleteCategory,
  votePoll,
  getPoll,
  toggleReaction,
  getReactions,
  uploadNewsImage,
  deleteNewsImage,
  NewsItem,
  NewsCategory,
  NewsPoll
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isAdminOrDC, canCreateNews } from '../utils/roleUtils';
import DEPLOYMENT_CONFIG from '../config/deployment';
import NewsSection from './NewsSection';
import EditNewsModal from './EditNewsModal';

const { Title, Text } = Typography;

interface LeaderboardEntry {
  id: number;
  user_id: number;
  installation_count: number;
  user_name: string;
  user_avatar: string | null;
  rank: number;
}

const MainPage: React.FC = () => {
  const { user } = useCognitoAuth();
  const navigate = useNavigate();
  const isAdminOrDCFlag = user ? isAdminOrDC(getUserRole(user)) : false;
  const isAdminUser = user ? isAdmin(getUserRole(user)) : false;

  // State for main page content
  const [mainPageContent, setMainPageContent] = useState({
    important_news: '',
    main_news: '',
    ticker_news: [],
    last_updated: null,
    updated_by: null
  });

  // State for editing
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editMainNewsModalOpen, setEditMainNewsModalOpen] = useState(false);
  const [editImportantValue, setEditImportantValue] = useState('');
  const [pendenteCount, setPendenteCount] = useState<number>(0);
  const [followUpCount, setFollowUpCount] = useState<number>(0);
  const [instalacaoCount, setInstalacaoCount] = useState<number>(0);
  const [tasksInProgressCount, setTasksInProgressCount] = useState<number>(0);
  const [pendingTasksCount, setPendingTasksCount] = useState<number>(0);
  const [todayEventsCount, setTodayEventsCount] = useState<number>(0);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardView, setLeaderboardView] = useState<'top10' | 'myRank'>('top10');
  const [myRank, setMyRank] = useState<number | null>(null);
  const [manageModalVisible, setManageModalVisible] = useState(false);
  const [allScores, setAllScores] = useState<LeaderboardEntry[]>([]);
  const [editingScores, setEditingScores] = useState<{ [key: number]: number }>({});

  // Scroll animation refs
  const heroRef = useRef<HTMLDivElement>(null);
  const newsRef = useRef<HTMLDivElement>(null);
  const mainPageContainerRef = useRef<HTMLDivElement>(null);

  // Navigation state
  const [currentSection, setCurrentSection] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // News section state
  const [newsSearchQuery, setNewsSearchQuery] = useState<string>('');
  const [newsCategory, setNewsCategory] = useState<string>('all');
  const [newsEmail, setNewsEmail] = useState<string>('');
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [newsItems, setNewsItems] = useState<Array<{ date: string; title: string; description: string }>>([]);
  const [editNewsModalOpen, setEditNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);

  // Fetch main page content on component mount
  useEffect(() => {
    const fetchMainPageContent = async () => {
      try {
        const content = await getMainPageContent();
        setMainPageContent(content);
        setEditImportantValue(content.important_news || '');
      } catch (error) {
      }
    };

    fetchMainPageContent();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        if (response.success) {
          setCategories(response.data);
        }
      } catch (error) {
        // Silently fail
      }
    };
    fetchCategories();
  }, []);

  // Fetch lead counts for notification
  useEffect(() => {
    let mounted = true;
    if (user && user.id) {
      apiService.getLeads(user.business_area, getUserIdAsNumber(user))
        .then(leads => {
          if (!mounted) return;
          const leadsArray = leads || [];
          
          // Count leads by status
          const pendenteCount = leadsArray.filter((lead: Lead) => lead.lead_status === 'Pendente').length;
          const followUpCount = leadsArray.filter((lead: Lead) => lead.lead_status === 'Follow-up').length;
          const instalacaoCount = leadsArray.filter((lead: Lead) => lead.lead_status === 'Por Instalar').length;
          
          setPendenteCount(pendenteCount);
          setFollowUpCount(followUpCount);
          setInstalacaoCount(instalacaoCount);
        })
        .catch(err => {
          if (mounted) {
            setPendenteCount(0);
            setFollowUpCount(0);
            setInstalacaoCount(0);
          }
        });
    } else {
      setPendenteCount(0);
      setFollowUpCount(0);
      setInstalacaoCount(0);
    }
    return () => { mounted = false; };
  }, [user]);

  // Fetch task counts for notification
  useEffect(() => {
    let mounted = true;
    if (user && user.id) {
      apiService.getTaskStatistics()
        .then(response => {
          if (!mounted) return;
          if (response.success && response.statistics) {
            const stats = response.statistics;
            // Count tasks in progress (pending + in_progress)
            const inProgressCount = (stats.assigned_to_me?.pending || 0) + (stats.assigned_to_me?.in_progress || 0);
            setTasksInProgressCount(inProgressCount);
            // Count pending tasks only
            const pendingCount = stats.assigned_to_me?.pending || 0;
            setPendingTasksCount(pendingCount);
          }
        })
        .catch(err => {
          if (mounted) {
            setTasksInProgressCount(0);
            setPendingTasksCount(0);
          }
        });
    } else {
      setTasksInProgressCount(0);
      setPendingTasksCount(0);
    }
    return () => { mounted = false; };
  }, [user]);

  // Fetch today's events count
  useEffect(() => {
    let mounted = true;
    if (user && user.id) {
      apiService.getActivities(getUserIdAsNumber(user))
        .then(activities => {
          if (!mounted) return;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          // Filter events for today
          const todayEvents = activities.filter((activity: any) => {
            if (!activity.due_date) return false;
            const eventDate = new Date(activity.due_date);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate >= today && eventDate < tomorrow;
          });
          
          setTodayEventsCount(todayEvents.length);
        })
        .catch(err => {
          if (mounted) {
            setTodayEventsCount(0);
          }
        });
    } else {
      setTodayEventsCount(0);
    }
    return () => { mounted = false; };
  }, [user]);

  // Fetch leaderboard data - load immediately when user logs in
  useEffect(() => {
    if (!user) return; // Wait for user to be available

    const fetchLeaderboard = async () => {
      try {
        setLeaderboardLoading(true);
        const response = await apiService.getTopTenLeaderboard();
        if (response.success) {
          // Filter out users with score = 0
          const filteredLeaderboard = response.leaderboard.filter((entry: LeaderboardEntry) => entry.installation_count > 0);
          setLeaderboard(filteredLeaderboard);
          
          // Find user's rank - only fetch all scores if needed
          if (user && user.id) {
            const userEntry = response.leaderboard.find((entry: LeaderboardEntry) => entry.user_id === getUserIdAsNumber(user));
            if (userEntry) {
              setMyRank(userEntry.rank);
            }
          }
        }
      } catch (error) {
      } finally {
        setLeaderboardLoading(false);
      }
    };

    fetchLeaderboard();
  }, [user]);

  // Section visibility animations
  useEffect(() => {
    const refs = [heroRef, newsRef];
    refs.forEach((ref, index) => {
      if (ref.current) {
        if (index === currentSection) {
          ref.current.classList.add('section-visible');
          ref.current.classList.remove('section-hidden');
        } else {
          ref.current.classList.remove('section-visible');
          ref.current.classList.add('section-hidden');
        }
      }
    });
  }, [currentSection]);

  const handleEditImportant = () => {
    setEditImportantValue(mainPageContent.important_news || '');
    setEditModalOpen(true);
  };


  // Helper function to parse news string (if needed for legacy format)
  const parseNewsString = (newsString: string): Array<{ date: string; title: string; description: string }> => {
    if (!newsString) return [];
    // Simple parser - adjust based on your actual format
    try {
      const lines = newsString.split('\n').filter(line => line.trim());
      const items: Array<{ date: string; title: string; description: string }> = [];
      let currentItem: { date: string; title: string; description: string } | null = null;
      
      for (const line of lines) {
        if (line.match(/^\d{2}\/\d{2}\/\d{4}/) || line.toLowerCase().includes('hoje')) {
          if (currentItem) items.push(currentItem);
          currentItem = { date: line.split(' - ')[0] || 'Hoje', title: '', description: '' };
          const rest = line.split(' - ').slice(1).join(' - ');
          if (rest) currentItem.title = rest;
        } else if (currentItem) {
          if (!currentItem.title) {
            currentItem.title = line;
          } else {
            currentItem.description += (currentItem.description ? '\n' : '') + line;
          }
        }
      }
      if (currentItem) items.push(currentItem);
      return items.length > 0 ? items : [{ date: 'Hoje', title: '', description: '' }];
    } catch {
      return [{ date: 'Hoje', title: '', description: '' }];
    }
  };

  const handleEditMainNews = () => {
    // Open the edit news modal for creating new news
    setEditingNews(null);
    setEditNewsModalOpen(true);
  };

  const handleNewsSuccess = () => {
    // Refresh news after successful edit/create
    setEditNewsModalOpen(false);
    setEditingNews(null);
    // Refetch categories to update the news board
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        if (response.success) {
          setCategories(response.data);
        }
      } catch (error) {
        // Silently fail
      }
    };
    fetchCategories();
    // The NewsSection component will automatically refresh
  };

  const handleSaveImportant = async () => {
    try {
      const updatedContent = await updateMainPageContent({
        important_news: editImportantValue
      });
      setMainPageContent(updatedContent);
      setEditModalOpen(false);
    } catch (error) {
    }
  };


  // Helper function to generate notification text
  const generateNotificationText = () => {
    const items = [];
    
    if (pendenteCount > 0) {
      items.push(`${pendenteCount} ${pendenteCount === 1 ? 'situação pendente' : 'situações pendentes'}`);
    }
    
    if (followUpCount > 0) {
      items.push(`${followUpCount} ${followUpCount === 1 ? 'follow-up marcado' : 'follow-ups marcados'}`);
    }
    
    if (instalacaoCount > 0) {
      items.push(`${instalacaoCount} ${instalacaoCount === 1 ? 'instalação marcada' : 'instalações marcadas'}`);
    }
    
    if (items.length === 0) {
      return null;
    }
    
    return `Tens:\n${items.map(item => `• ${item};`).join('\n')}`;
  };

  const generateTaskNotificationText = () => {
    if (tasksInProgressCount > 0) {
      return `Tens ${tasksInProgressCount} ${tasksInProgressCount === 1 ? 'tarefa em progresso' : 'tarefas em progresso'}`;
    }
    return null;
  };

  const getAvatarUrl = (avatarUrl: string | null | undefined) => {
    if (avatarUrl) {
      if (avatarUrl.startsWith('data:image/')) {
        return avatarUrl;
      }
      return `${DEPLOYMENT_CONFIG.getBackendUrl()}/${avatarUrl}`;
    }
    return undefined;
  };

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    return '#1890ff';
  };

  const handleManageScores = async () => {
    try {
      const response = await apiService.getAllScores();
      if (response.success) {
        setAllScores(response.scores);
        const initialScores: { [key: number]: number } = {};
        response.scores.forEach((score: LeaderboardEntry) => {
          initialScores[score.user_id] = score.installation_count;
        });
        setEditingScores(initialScores);
        setManageModalVisible(true);
      }
    } catch (error) {
      message.error('Erro ao carregar pontuações');
    }
  };

  const handleScoreChange = (userId: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setEditingScores(prev => ({
      ...prev,
      [userId]: numValue
    }));
  };

  const handleSaveScores = async () => {
    try {
      const promises = Object.entries(editingScores).map(([userId, count]) =>
        apiService.updateUserScore(parseInt(userId), count)
      );
      
      await Promise.all(promises);
      message.success('Pontuações atualizadas com sucesso!');
      setManageModalVisible(false);
      // Refresh leaderboard
      const response = await apiService.getTopTenLeaderboard();
      if (response.success) {
        // Filter out users with score = 0
        const filteredLeaderboard = response.leaderboard.filter((entry: LeaderboardEntry) => entry.installation_count > 0);
        setLeaderboard(filteredLeaderboard);
      }
    } catch (error) {
      message.error('Erro ao atualizar pontuações');
    }
  };

  // Navigation handler
  const handleNavigation = (sectionIndex: number) => {
    if (isTransitioning || sectionIndex === currentSection) return;
    
    setIsTransitioning(true);
    
    // Apply motion blur
    if (mainPageContainerRef.current) {
      mainPageContainerRef.current.style.filter = 'blur(6px)';
      mainPageContainerRef.current.style.transition = 'filter 0.15s cubic-bezier(0.4, 0, 0.2, 1)';
    }
    
    setTimeout(() => {
      setCurrentSection(sectionIndex);
      
      setTimeout(() => {
        if (mainPageContainerRef.current) {
          mainPageContainerRef.current.style.filter = 'blur(0px)';
        }
        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
      }, 50);
    }, 150);
  };

  const sections = [
    { name: 'Início', icon: <RocketOutlined /> },
  ];

  return (
    <div className="modern-main-page" ref={mainPageContainerRef}>
      {/* Hero Section */}
      <section 
        ref={heroRef} 
        className={`hero-section ${currentSection === 0 ? 'section-active' : 'section-hidden'}`}
        style={{ display: currentSection === 0 ? 'flex' : 'none' }}
      >
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Bem-vindo(a){user && user.username ? `, ${user.username}!` : '!'}
            </h1>
          </div>
          <div className="hero-stats">
            <div className="stat-card">
              <div className="stat-value">{pendingTasksCount}</div>
              <div className="stat-label">Tarefas Pendentes</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{todayEventsCount}</div>
              <div className="stat-label">Eventos para hoje</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{pendenteCount}</div>
              <div className="stat-label">Leads pendentes</div>
            </div>
          </div>
        </div>
        
        {/* Quick Actions - Now part of Hero Section */}
        <div className="hero-actions-container">
          <div className="actions-grid">
            {isAdminUser ? (
              <>
                <Card 
                  className="action-card create-lead" 
                  hoverable 
                  onClick={() => navigate('/leads')}
                >
                  <div className="action-icon-wrapper">
                    <PlusCircleOutlined className="action-icon" />
                  </div>
                  <h3 className="action-title">Criar Lead</h3>
                  <p className="action-description">Adiciona um novo lead ao sistema</p>
                </Card>

                <Card 
                  className="action-card client-management" 
                  hoverable 
                  onClick={() => navigate('/client-management')}
                >
                  <div className="action-icon-wrapper">
                    <FileTextOutlined className="action-icon" />
                  </div>
                  <h3 className="action-title">Gestão de Clientes</h3>
                  <p className="action-description">
                    {generateNotificationText() ? (
                      <span className="notification-text">{generateNotificationText()}</span>
                    ) : (
                      <span className="no-activity">Nenhuma atividade pendente</span>
                    )}
                  </p>
                </Card>

                <Card 
                  className="action-card calendar" 
                  hoverable 
                  onClick={() => navigate('/calendar')}
                >
                  <div className="action-icon-wrapper">
                    <CalendarOutlined className="action-icon" />
                  </div>
                  <h3 className="action-title">Ver Calendário</h3>
                  <p className="action-description">Visualiza e gere o teu calendário</p>
                </Card>

                <Card 
                  className="action-card taskmaster" 
                  hoverable 
                  onClick={() => navigate('/taskmaster')}
                >
                  <div className="action-icon-wrapper">
                    <CheckSquareOutlined className="action-icon" />
                  </div>
                  <h3 className="action-title">Gestor de Tarefas</h3>
                  <p className="action-description">
                    {generateTaskNotificationText() ? (
                      <span className="notification-text">{generateTaskNotificationText()}</span>
                    ) : (
                      <span className="no-activity">Nenhuma tarefa em progresso</span>
                    )}
                  </p>
                </Card>
              </>
            ) : (
              <Card 
                className="action-card client-management" 
                hoverable 
                onClick={() => navigate('/client-workspace')}
              >
                <div className="action-icon-wrapper">
                  <CustomerServiceOutlined className="action-icon" />
                </div>
                <h3 className="action-title">Workspace de Cliente</h3>
                <p className="action-description">Acede ao workspace de cliente</p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Leaderboard Section (Ranking) - Commented out as requested */}
      {/*
      <section
        className={`leaderboard-section ${currentSection === 1 ? 'section-active' : 'section-hidden'}`}
        style={{ display: currentSection === 1 ? 'block' : 'none' }}
      >
        <div className="section-header">
          <div className="leaderboard-controls">
            {isAdminOrDCFlag && (
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={handleManageScores}
                className="control-btn"
              >
                Gerir
              </Button>
            )}
          </div>
        </div>

        <div className="leaderboard-container">
          ... ranking content removed ...
        </div>
      </section>
      */}

      {/* News Section */}
      <section 
        ref={newsRef} 
        className={`news-section ${currentSection === 1 ? 'section-active' : 'section-hidden'}`}
        style={{ display: currentSection === 1 ? 'block' : 'none' }}
      >
        <div className="news-section-container">
          {/* Decorative background elements */}
          <div className="news-bg-decoration news-bg-circle-1"></div>
          <div className="news-bg-decoration news-bg-circle-2"></div>
          <div className="news-bg-decoration news-bg-circle-3"></div>

          <div className="news-content-wrapper">
            <NewsSection
              categories={categories}
              selectedCategory={newsCategory}
              onCategoryChange={setNewsCategory}
              onEditClick={handleEditMainNews}
              canEdit={user ? canCreateNews(getUserRole(user)) : false}
            />
          </div>
        </div>
      </section>

      {/* Modals */}
      <Modal 
        title="Editar Notícias Importantes" 
        open={editModalOpen} 
        onOk={handleSaveImportant} 
        onCancel={() => setEditModalOpen(false)} 
        okText="Salvar" 
        cancelText="Cancelar"
      >
        <Input.TextArea 
          rows={2} 
          value={editImportantValue} 
          onChange={e => setEditImportantValue(e.target.value)} 
          placeholder="Digite a notícia importante" 
        />
      </Modal>

      <EditNewsModal
        open={editNewsModalOpen}
        onClose={() => {
          setEditNewsModalOpen(false);
          setEditingNews(null);
        }}
        onSuccess={handleNewsSuccess}
        editingNews={editingNews}
      />

      {/* Manage Scores Modal */}
      <Modal
        title="Gerir Pontuações de Instalações"
        open={manageModalVisible}
        onOk={handleSaveScores}
        onCancel={() => setManageModalVisible(false)}
        okText="Salvar"
        cancelText="Cancelar"
        width={800}
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
            gap: '16px', 
            padding: '16px' 
          }}>
            {allScores.map((entry) => (
              <div 
                key={entry.user_id} 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  padding: '20px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '12px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.borderColor = '#1890ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                }}
              >
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '13px', 
                  textAlign: 'center',
                  marginBottom: '12px',
                  color: '#333'
                }}>
                  {entry.user_name}
                </div>
                <Input
                  type="number"
                  min={0}
                  value={editingScores[entry.user_id] || 0}
                  onChange={(e) => handleScoreChange(entry.user_id, e.target.value)}
                  style={{ 
                    width: '100px', 
                    textAlign: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  size="large"
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Navigation Buttons */}
      <div className="page-navigation">
        {sections.map((section, index) => (
          <Button
            key={index}
            type={currentSection === index ? 'primary' : 'default'}
            icon={section.icon}
            onClick={() => handleNavigation(index)}
            className={`nav-button ${currentSection === index ? 'active' : ''}`}
            disabled={isTransitioning}
            title={section.name}
          >
            {section.name}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default MainPage;
